<script lang="ts">
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import type { SwimFirehoseResponse, LapLabel } from "@contract/types";
  import type { Lap } from "../lib/laps";
  import { bridge, BridgeUnavailableError } from "../bridge/client";
  import { deriveWorkout, type DerivedWorkout } from "../lib/lapAnalysis";
  import { serializeExport } from "../lib/share";
  import { fetchFirehoseWithRetry } from "../lib/firehoseRetry";
  import { formatWorkoutDay } from "../lib/format";
  import SwipeDeck from "../lib/SwipeDeck.svelte";
  import {
    selectedWorkoutUUID,
    currentDerivedWorkout,
    currentRawFirehose,
    currentFirehoseStatus,
    currentLabels,
    labelsLoadFailed,
  } from "../lib/state";
  import { initSetsDiscovered } from "../lib/setsDiscovered";
  import { recordStrokeStats } from "../lib/strokeStats";
  import { recordSwimFacts } from "../lib/allTimeAggregates";
  import { resetSaveStatusForSwim, resolveLabelsOnLoad } from "../lib/liveCorrection";
  import { refetchWorkouts } from "../lib/workoutsStore";
  import { handleAuthResolved } from "../lib/authResolved";
  import P1StrokeRibbon from "./prototypes/P1StrokeRibbon.svelte";
  import P2HrTimeline from "./prototypes/P2HrTimeline.svelte";
  import P3LapHeatmap from "./prototypes/P3LapHeatmap.svelte";
  import P4EffortHero from "./prototypes/P4EffortHero.svelte";
  import P5StrokeDonut from "./prototypes/P5StrokeDonut.svelte";
  import P6Swolf from "./prototypes/P6Swolf.svelte";
  import P7SilkRing from "./prototypes/P7SilkRing.svelte";
  import { debugSearch } from "../lib/debugSearch";

  // `showcase` (default false) is the PUBLIC-page flag. When true, the data
  // engine below STILL runs (hero card + set ribbon read its stores), but none
  // of this component's markup renders, so the public page shows only the swim.
  // Production callers pass nothing → false → unchanged.
  // `headless` (default false) runs the ENGINE with no markup at all. The
  // native-tab Scores view mounts it this way: it needs the
  // firehose->derive->publish pipeline that fills currentDerivedWorkout, but
  // not a second (empty) prototypes section in the page. See App.svelte.
  let { showcase = false, headless = false }: { showcase?: boolean; headless?: boolean } = $props();

  let raw: SwimFirehoseResponse | null = $state(null);
  let derived: DerivedWorkout | null = $state(null);
  let loading = $state(true);
  let error: string | null = $state(null);
  let exportStatus: string | null = $state(null);
  // Bumped by the auth-resolved nudge (G2b) to re-run the firehose fetch effect
  // below when the first-launch permission sheet is dismissed after Task 3's
  // retry window has already given up.
  let authRefetchNudge = $state(0);

  // ?proto=1 reveals the experimental chart deck (P1–P7) + the inline export,
  // in a dev build only. With `showcase` it is the ONE gate for every piece of
  // markup below (the `{#if}` that opens it). The data engine (firehose load → derive → publish
  // stores) ALWAYS runs regardless — the hero card, set ribbon, rings, Firehose
  // and Share all read those stores, so this component stays mounted even when
  // the charts are hidden.
  const showProto = new URLSearchParams(debugSearch()).get("proto") === "1";

  // The live persisted-labels array (D-15). Seeded from the bridge on load and
  // mutated by the swipe deck; every change re-runs deriveWorkout below so SWOLF
  // recomputes LIVE. Replaces the Plan 01 `[]` seam.
  let labels: LapLabel[] = $state([]);

  /** Re-derive from the current raw firehose + live labels and republish. */
  function rederive() {
    if (!raw) return;
    derived = deriveWorkout(raw, labels);
    currentDerivedWorkout.set(derived);
    // Write path B: update stroke stats + swim facts whenever derivation
    // refreshes (a label edit changes SWOLF and can change a record — e.g. a
    // corrected stroke rewrites the fly streak). Idempotent UPSERTs by uuid.
    recordStrokeStats(derived, raw.workout.uuid, raw.workout.startDate);
    recordSwimFacts(derived, raw.workout.uuid, raw.workout.startDate);
  }

  /** Deck callback: reassign labels then re-derive immediately (optimistic). */
  function onLabelsChange(next: LapLabel[]) {
    labels = next;
    currentLabels.set(next);   // keep the shared store in lockstep with the deck
    rederive();
  }

  /** The flagged-metric line for a review card (D-09 fact 3). Prefer the reason
   *  the exclusion pass already stamped (jargon-free); fall back to a neutral
   *  line. Never invents a SWOLF number the tester can't audit. */
  function flagLineFor(lap: Lap): string {
    if (lap.exclusionReason) return lap.exclusionReason;
    if (lap.strokeStyle === "Unknown") return "No stroke detected";
    return `Length ${lap.index} flagged`;
  }

  // Presentation helpers — called inline from the template. Svelte 5's
  // fine-grained reactivity re-invokes them when `raw` changes.
  function workoutNameFor(r: SwimFirehoseResponse | null): string {
    if (!r) return "";
    return formatWorkoutDay(r.workout.startDate);
  }
  function exportSizeFor(r: SwimFirehoseResponse | null): string {
    if (!r) return "";
    return `${(new Blob([JSON.stringify(r)]).size / 1024).toFixed(0)} KB`;
  }

  // Mount the sets-discovered data engine once. Prototypes is the always-mounted
  // engine (the firehose→derive→publish pipeline runs even when the ?proto charts
  // are hidden), so it owns this init. initSetsDiscovered is idempotent against
  // re-mounts and subscribes to currentRawFirehose to fold each swim in.
  onMount(() => {
    initSetsDiscovered();
    // G2b: native fires `swimset:auth-resolved` the moment the first-launch
    // HealthKit permission sheet is dismissed (grant OR deny — read-auth status
    // is unobservable). Refetch the workout list past its one-shot guard, and
    // re-run THIS swim's firehose fetch only if it came back empty/errored.
    // Idempotent: a swim that already loaded is a no-op (no duplicate publish).
    const onAuthResolved = () =>
      handleAuthResolved({
        refetchWorkouts,
        firehoseStatus: () => get(currentFirehoseStatus),
        refetchFirehose: () => { authRefetchNudge += 1; },
      });
    window.addEventListener("swimset:auth-resolved", onAuthResolved);
    return () => window.removeEventListener("swimset:auth-resolved", onAuthResolved);
  });

  // Fetch the firehose whenever the selected swim changes. `null` selection
  // means "most recent swim"; an explicit UUID hits the bridge cache after the
  // first time. Cache is keyed inside bridge/client.ts.
  $effect(() => {
    const uuid = $selectedWorkoutUUID;
    void authRefetchNudge; // re-run this fetch when an auth-resolved nudge bumps it (G2b)
    let cancelled = false;
    loading = true;
    error = null;
    currentFirehoseStatus.set("loading");
    (async () => {
      try {
        if (!bridge.isAvailable()) {
          if (!cancelled) {
            error = "Bridge unavailable.";
            currentFirehoseStatus.set("error");
          }
          return;
        }
        // G2: retry the fetch across the first-launch auth-resolution window so a
        // real swim is never permanently blanked by an empty first healthd query
        // or the 5s bridge timeout.
        const { result: res, error: fetchErr } = await fetchFirehoseWithRetry(
          () => bridge.getWorkoutFirehose(uuid ? { workoutUUID: uuid } : {}),
          uuid !== null,
          { isCancelled: () => cancelled },
        );
        if (cancelled) return;
        if (fetchErr) throw fetchErr;
        if (!res) {
          error = "No swim workout in HealthKit.";
          raw = null;
          derived = null;
          currentDerivedWorkout.set(null);
          currentRawFirehose.set(null);
          currentFirehoseStatus.set("empty");
          return;
        }
        // Seed the live labels from the persisted store BEFORE the first derive
        // so D-04 overrides apply on load. C2a: a FAILED load is NOT the same as
        // an empty swim — flag it so commitLabelChange refuses to write (and thus
        // wipe) the labels we couldn't read; a genuinely label-less swim loads [].
        // Bounded retry mirrors the firehose fetch so a slow first read during the
        // auth-resolution window can't wrongly latch labelsLoadFailed.
        //
        // resolveLabelsOnLoad returns the LIVE currentLabels when this is the SAME
        // swim already in memory (e.g. this effect re-running after the Swim panel
        // remounts on a Sets->Swim tab switch). That keeps a just-made correction
        // the in-memory store already holds from being reverted by a native read
        // that lags the optimistic write. A different/first swim reads native.
        const { labels: loadedLabels, failed: loadFailed } = await resolveLabelsOnLoad(
          res.workout.uuid,
          { isCancelled: () => cancelled },
        );
        labels = loadedLabels;
        if (cancelled) return;
        currentLabels.set(labels);   // shared source of truth for the overlay's corrections
        labelsLoadFailed.set(loadFailed);
        // Swim-aware reset (not resetSaveStatus): a same-swim remount (this effect
        // re-running on a Sets->Swim tab switch) must not disarm a HEALTHY in-flight
        // save's "saving" suppression — that disarming is what let the false
        // "didn't save" pill fire (audit 2026-07-12). Only a genuine swim-switch
        // clears prior save state (T5b).
        resetSaveStatusForSwim(res.workout.uuid);
        raw = res;
        derived = deriveWorkout(res, labels);   // live persisted labels (D-15) — Plan 01 `[]` seam replaced
        currentDerivedWorkout.set(derived);
        // Write path B: record this swim's per-stroke stats + countable facts
        // on every firehose open.
        recordStrokeStats(derived, res.workout.uuid, res.workout.startDate);
        recordSwimFacts(derived, res.workout.uuid, res.workout.startDate);
        currentRawFirehose.set(res);
        currentFirehoseStatus.set("ready");
      } catch (err) {
        if (cancelled) return;
        if (err instanceof BridgeUnavailableError) {
          error = `${err.reason}: ${err.message}`;
        } else {
          error = err instanceof Error ? err.message : String(err);
        }
        // HYG-05: a failed refetch must never leave the previous swim visible.
        currentDerivedWorkout.set(null);
        currentRawFirehose.set(null);
        currentFirehoseStatus.set("error");
      } finally {
        if (!cancelled) loading = false;
      }
    })();
    return () => { cancelled = true; };
  });

  async function copyJson() {
    if (!raw) return;
    // HYG-04: emit the versioned export envelope, not the bare firehose.
    const json = serializeExport(raw);
    try {
      await navigator.clipboard.writeText(json);
      exportStatus = `Copied ${(new Blob([json]).size / 1024).toFixed(0)} KB to clipboard`;
      setTimeout(() => (exportStatus = null), 3500);
    } catch (err) {
      exportStatus = `Clipboard blocked — use Share button instead`;
    }
  }

  async function shareJson() {
    if (!raw) return;
    // HYG-04: emit the versioned export envelope, not the bare firehose.
    const json = serializeExport(raw);
    const filename = `swim-firehose-${new Date(raw.workout.startDate).toISOString().slice(0, 10)}.json`;
    const blob = new Blob([json], { type: "application/json" });
    // Web Share API with files (iOS 15+)
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (typeof nav.share === "function") {
      const file = new File([blob], filename, { type: "application/json" });
      const shareData: ShareData = { files: [file], title: "Swim firehose", text: `Swim Set Club firehose · ${workoutNameFor(raw)}` };
      if (!nav.canShare || nav.canShare(shareData)) {
        try {
          await nav.share(shareData);
          exportStatus = "Shared";
          setTimeout(() => (exportStatus = null), 2500);
          return;
        } catch {
          // user cancelled — silently fall through
        }
      }
    }
    // Fallback: trigger download via blob URL
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    exportStatus = "Downloaded";
    setTimeout(() => (exportStatus = null), 2500);
  }
</script>

<!-- Only under ?proto=1 in a dev build, never on the showcase: this `{#if}` is the
     one gate, so nothing inside needs its own. This used to open on
     `showProto || derived`, which on every normal Swim tab with a loaded swim
     rendered an EMPTY section — and app.css's `#app-root section[class]` hairline
     drew that as a 358x18 bordered box above "This swim · <date>" (Build 22
     review; Scott: "Fix it in 22"). Nothing is lost: every use of `derived` that
     matters (the currentDerivedWorkout store write, recordStrokeStats,
     recordSwimFacts) is in the <script> and runs regardless of this markup.
     Pinned in Chromium by web/scripts/_emptybox_probe.mjs (the section is absent
     without ?proto=1) and by prototypesShowcaseGate.test.ts (absent on the
     showcase even with it). -->
{#if !headless && !showcase && showProto}
<section class="protos">
  <header class="protos-header">
    <h1>Your Swim</h1>
    <p class="muted">
      Six ways to read the same workout. Effort first, then HR + phases, then SWOLF efficiency,
      then the structure / lap details.
      {#if raw}<span class="anchor">· {workoutNameFor(raw)}</span>{/if}
    </p>
  </header>

  {#if loading}
    <p class="muted">Loading…</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if derived}
    <!-- G7 (Wave 0): the legacy inline SwipeDeck no longer renders on the Swim
         tab. The single correction path is now the StrokeSuspectsCard -> suspect
         deck overlay. The file is kept (not deleted) and stays reachable ONLY
         under ?proto=1 so its keep/exclude + custom-naming tools survive until
         Wave 2 folds them into the Fix-Strokes flow. -->
    <SwipeDeck
      laps={derived.laps}
      reviewLapIndices={derived.reviewLapIndices}
      {flagLineFor}
      {labels}
      {onLabelsChange}
    />
    <P7SilkRing dw={derived} />
    <P4EffortHero dw={derived} />
    <P2HrTimeline dw={derived} />
    <P6Swolf dw={derived} />
    <P1StrokeRibbon dw={derived} />
    <P3LapHeatmap dw={derived} />
    <P5StrokeDonut dw={derived} />

    <div class="export">
      <div class="export-title">Share this swim's data</div>
      <p class="export-sub muted">
        Sends the full firehose JSON ({exportSizeFor(raw)}) so Claude can read your real lap-by-lap data
        and write a story-annotated chart.
      </p>
      <div class="export-buttons">
        <button onclick={copyJson}>📋 Copy JSON</button>
        <button onclick={shareJson} class="primary">↗ Share file</button>
      </div>
      {#if exportStatus}
        <div class="export-status">{exportStatus}</div>
      {/if}
    </div>
  {/if}
</section>
{/if}

<style>
  .protos { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid var(--bg-raised); }
  .protos-header { margin-bottom: 0.5rem; }
  h1 { font-size: 1.6rem; font-weight: 800; margin: 0; letter-spacing: -0.02em; }
  .protos-header .muted { font-size: 0.8rem; margin: 0.25rem 0 0; }
  .anchor { color: var(--accent-primary); }
  .muted { color: var(--text-muted); }
  .error { color: var(--accent-fire); }

  .export {
    margin-top: 1rem;
    padding: 1rem;
    background: var(--bg-card);
    border-radius: 12px;
    border: 1px dashed var(--bg-raised);
  }
  .export-title { font-size: 0.9rem; font-weight: 700; }
  .export-sub { font-size: 0.75rem; margin: 0.25rem 0 0.75rem; line-height: 1.4; }
  .export-buttons { display: flex; gap: 0.5rem; }
  .export-buttons button {
    flex: 1; padding: 0.6rem 0.75rem;
    background: var(--bg-raised); color: var(--text-primary);
    border: 0; border-radius: 8px; font-size: 0.85rem; font-weight: 600;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  .export-buttons button.primary { background: var(--accent-fire); color: var(--bg-canvas); }
  .export-status {
    margin-top: 0.6rem; font-size: 0.78rem;
    color: var(--accent-primary); font-weight: 600; text-align: center;
  }
</style>
